import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Search } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { LOGO_HEADER } from "../mock";
import SpinningLogo from "./SpinningLogo";

const buildNav = (sections) => [
  { to: "/shop", label: "All Products" },
  ...sections.map((s) => ({ to: `/shop/${s.key}`, label: s.label, sale: s.key === "sale" })),
  { to: "/our-story", label: "Our Story" },
  { to: "/the-hidden-truth", label: "The Hidden Truth" },
  { to: "/faq", label: "FAQ" },
];

const Announcement = () => (
  <div className="overflow-hidden bg-[#1B4332] py-2 text-white">
    <div className="animate-marquee">
      {Array.from({ length: 2 }).map((_, i) => (
        <span key={i} className="flex items-center">
          {[
            "CLEAN · HIGH STRENGTH · NASTY FREE",
            "MADE IN THE UK 🌿",
            "NO MAGNESIUM STEARATE · NO FILLERS",
            "FREE UK SHIPPING OVER £40",
            "46,500+ FIVE-STAR REVIEWS",
          ].map((t, j) => (
            <span key={j} className="mx-6 font-display text-xs uppercase tracking-[0.25em] text-[#B7E4C7]">
              {t}
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

const Header = () => {
  const { count, setOpen } = useCart();
  const { sections } = useStore();
  const NAV = buildNav(sections);
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50">
      <Announcement />
      <div className="border-b border-[#e3efe7] bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setMobile(true)} aria-label="Menu">
            <Menu size={24} className="text-[#1B4332]" />
          </button>

          <Link to="/" className="flex items-center gap-3">
            <SpinningLogo size={52} ring="ring-[#e3efe7]" />
            <img src={LOGO_HEADER} alt="British Supplements — Quality, Potent, Clean" className="h-14 w-auto object-contain sm:h-16" />
          </Link>

          <div className="flex items-center gap-4">
            <button className="hidden text-[#2D6A4F] transition hover:text-[#1B4332] sm:block" aria-label="Search">
              <Search size={20} />
            </button>
            <button onClick={() => setOpen(true)} className="relative text-[#1B4332] transition hover:text-[#2D6A4F]" aria-label="Cart">
              <ShoppingBag size={22} />
              {count > 0 && (
                <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#52B788] px-1 text-[11px] font-bold text-white">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        <nav className="hidden border-t border-[#eef5f0] lg:block">
          <div className="mx-auto flex max-w-7xl items-center justify-center gap-7 px-6 py-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `font-display text-[13px] font-semibold uppercase tracking-wider transition hover:text-[#2D6A4F] ${
                    n.sale ? "text-[#c1121f]" : isActive ? "text-[#2D6A4F]" : "text-[#34463c]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobile && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobile(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-display text-lg font-bold uppercase text-[#1B4332]">Menu</span>
              <button onClick={() => setMobile(false)}><X size={22} className="text-[#1B4332]" /></button>
            </div>
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                <button
                  key={n.to}
                  onClick={() => { setMobile(false); navigate(n.to); }}
                  className={`rounded-lg px-3 py-3 text-left font-display text-sm font-semibold uppercase tracking-wide transition hover:bg-[#f0f7f2] ${n.sale ? "text-[#c1121f]" : "text-[#34463c]"}`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
